import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Link2, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';

export const BacklinkMonitoring = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [auditResult, setAuditResult] = useState<any>(null);

  const handleBacklinkAudit = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('seo-automation', {
        body: {
          action: 'backlink_audit',
          data: {}
        }
      });

      if (error) throw error;

      setAuditResult(data);
      toast({
        title: 'Audit Complete',
        description: `Analyzed ${data.backlink_count} backlinks`,
      });
    } catch (error) {
      console.error('Error:', error);
      toast({
        title: 'Error',
        description: 'Failed to perform backlink audit',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5" />
            Backlink Generation & Monitoring
          </CardTitle>
          <CardDescription>
            Automatically monitor backlink health and identify opportunities
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Button onClick={handleBacklinkAudit} disabled={loading} className="w-full">
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Running Backlink Audit...
              </>
            ) : (
              <>
                <Link2 className="w-4 h-4 mr-2" />
                Run Backlink Audit
              </>
            )}
          </Button>

          {auditResult && (
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                <div className="space-y-2">
                  <p className="font-semibold">
                    Analyzed {auditResult.backlink_count} backlinks
                  </p>
                  <div className="text-sm whitespace-pre-wrap">
                    {auditResult.analysis}
                  </div>
                </div>
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  High Quality Links
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">-</div>
                <p className="text-xs text-muted-foreground">Run audit to see results</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  Medium Quality
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">-</div>
                <p className="text-xs text-muted-foreground">Run audit to see results</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium">
                  Low Quality
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-red-600">-</div>
                <p className="text-xs text-muted-foreground">Run audit to see results</p>
              </CardContent>
            </Card>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};